import {
  type PreinitializedMapStore,
  type PreinitializedWritableAtom,
} from "nanostores";

import { isLocal } from "./global";
import { isTruthy } from "./isTruthy";

type ImportMetaHot = {
  accept(callback?: () => void): void;
  dispose(callback: () => void): void;
};

/**
 * Reusable function to handle Hot Module Replacement (HMR) for a nanostore,
 * persisting its state in localStorage.
 *
 * @param {import('nanostores').AnyStore} store - The nanostore to persist.
 * @param {string} storageKey - A unique key to use for localStorage.
 */
export function handleStoreHMR(
  store: PreinitializedMapStore | PreinitializedWritableAtom<unknown>,
  storageKey: string,
  hot?: ImportMetaHot, // Optional injection for test
) {
  if (!isLocal()) {
    return;
  }

  const saveStateToLocalStorage = () => {
    const state = store.get();
    localStorage.setItem(storageKey, JSON.stringify(state));
  };

  const loadStateFromLocalStorage = () => {
    const storedState = localStorage.getItem(storageKey);

    if (storedState === null || storedState === "undefined") return;

    if (isTruthy(storedState)) {
      try {
        store.set(JSON.parse(storedState));
      } catch (error) {
        console.error(
          `Error parsing stored state for key '${storageKey}':`,
          error,
        );
      }
    }
  };

  loadStateFromLocalStorage();
  store.subscribe(saveStateToLocalStorage);

  const hmr: ImportMetaHot | undefined =
    hot ?? (import.meta as ImportMeta & { hot?: ImportMetaHot }).hot;

  if (hmr) {
    hmr.dispose(() => {
      saveStateToLocalStorage();
    });

    hmr.accept(() => {
      loadStateFromLocalStorage();
    });
  }
}
